<template>
  <router-view />
</template>

<script setup>
import Parse from '@/store/parseInitialization';
import { useDoctorStore } from '@/store/doctor';
import { startVersionCheck } from '@/plugins/versionCheck';
import router from '@/router';
import { isPublicReportPath, shouldClearSessionOnBoot } from '@/lib/bootSession';

const doctor = useDoctorStore();

// Kick off the new-deploy poller. Single-shot guard inside the
// module - safe to call from the root component on every mount.
startVersionCheck();

// Log the doctor out when they left "remember this device" unticked, but
// only on a genuinely new visit - a refresh is not a tab close - and never
// on a path that is mid-flow. shouldClearSessionOnBoot owns those rules and
// is the only place they are asserted (lib/bootSession.test.ts).
window.addEventListener('load', async () => {
  const isReload = performance.getEntriesByType('navigation')[0].type === 'reload';

  if (shouldClearSessionOnBoot({
    rememberMe: doctor.rememberMe,
    isReload,
    pathname: window.location.pathname,
  })) {
    await doctor.logout();
    router.push({ name: 'home' });
    return;
  }

  // The public report pages need no clinician, and a stale session found here
  // would otherwise log out and navigate away from the report being read.
  if (isPublicReportPath(window.location.pathname)) return;

  // Only hydrate the profile when an authenticated Parse session exists.
  // The persisted Pinia store and the Parse SDK session token are separate:
  // without this guard, getProfile() calls a `requireUser: true` cloud
  // function with no session and Parse rejects with
  // "Validation failed. Please login to continue." as an uncaught rejection.
  if (!Parse.User.current()) {
    return;
  }

  try {
    // call profile
    await doctor.getProfile();
  } catch {
    // Session is stale/expired - fail soft. Clear it and let the router
    // guard send the user to /login on the next navigation.
    await doctor.logout();
    router.push({ name: 'home' });
  }
});
</script>
